// The 8 accepted wire intents. land:manage is NOT accepted (manage is commercial/residential only).
export const INTENTS = [
  "commercial:buy",
  "commercial:sell",
  "commercial:manage",
  "residential:buy",
  "residential:sell",
  "residential:manage",
  "land:buy",
  "land:sell",
] as const;

export type Intent = (typeof INTENTS)[number];
export type AssetType = "commercial" | "residential" | "land";
export type TxnType = "buy" | "sell" | "manage";

const INTENT_SET: ReadonlySet<string> = new Set(INTENTS);

export function isIntent(value: unknown): value is Intent {
  return typeof value === "string" && INTENT_SET.has(value);
}

// Decompose a wire intent into its asset/transaction parts. Throws on an unknown intent;
// callers validate with isIntent first (a 400 at the edge, never a decode of bad input).
export function decomposeIntent(intent: Intent): { assetType: AssetType; txnType: TxnType } {
  const [assetType, txnType] = intent.split(":") as [AssetType, TxnType];
  return { assetType, txnType };
}

// The 9th, invite-only intent (src/interview/catalog.ts's LEASING_TENANT_INTENT; shared/types.ts's
// LeadItem.intent comment). Restated here rather than imported: this module is a lean,
// dependency-free foundational type module read by nearly everything, and the catalog module (the
// full leasing questionnaire) is not something a Lambda bundle should pull in for one string.
export const LEASING_TENANT_INTENT = "leasing:tenant";

// ONE place for "does this lead's BROKER_RECAP get suppressed" (S8, evanhowell-v72w.9): a
// leasing:tenant lead's qualification is reported by the next morning's digest instead of a
// per-lead recap. taskMaker.ts's Filter A and backstopReconciler.ts's
// reconcileQualifiedUnnotifiedLead / clearQualifiedUnnotifiedIfSettled BOTH decide whether a
// BROKER_RECAP task should exist for a lead, and a duplicated inline check is exactly how those two
// drifted the first time: the reconciler recreated the recap task Filter A had deliberately never
// created, 15 minutes later, undoing the suppression this rule exists for. One function, so the
// next consumer of that same question cannot independently reach a different answer.
export function suppressesBrokerRecap(intent: string): boolean {
  return intent === LEASING_TENANT_INTENT;
}
